import type { AxisSpec } from "../ChartSpec";

/** Which names the legend spells out per series. All of them off means no legend at all. */
export type LegendParts = {
    categories: boolean;
    devices: boolean;
    sensorClusters: boolean;
    sensorElements: boolean;
};

/** Vertical room one value tag needs. Also the minimum gap when they are pushed apart. */
export const TAG_MIN_GAP_PX = 15;

export type PanelChrome = {
    /** Badge names the metric when every series measures the same thing. */
    metricCategory: string | null;
    /** Badge names the device when every series comes from one. */
    deviceName: string | null;
    legend: LegendParts;
    hoverTags: boolean;
    /** Tooltip box. Never on together with the tags — it would repeat them and cover the plot. */
    tooltip: boolean;
};

/** The names a stream can be told apart by. */
type StreamNames = {
    name: string;
    unitCategory: string;
    sensorClusterName?: string | null;
    sensorElementName?: string | null;
};

export function legendShowsAnything(parts: LegendParts): boolean {
    return parts.categories || parts.devices || parts.sensorClusters || parts.sensorElements;
}

/** True when the series do not all share one value. */
function varies(values: ReadonlyArray<string | null>): boolean {
    return values.length > 0 && values.some((value) => value !== values[0]);
}

/** True when no two legend rows would read the same with these parts on. */
function tellsStreamsApart(streams: ReadonlyArray<StreamNames>, parts: LegendParts): boolean {
    const rows = streams.map((s) =>
        [
            parts.categories ? s.unitCategory : "",
            parts.devices ? s.name : "",
            parts.sensorClusters ? s.sensorClusterName : "",
            parts.sensorElements ? s.sensorElementName : "",
        ].join("|"),
    );
    return new Set(rows).size === rows.length;
}

/**
 * Legend lists what varies between series; the badge names the metric and the device they share.
 *
 * The metric and the device always go in when they vary. The sensor and channel names are only
 * tiebreakers — a zone panel whose series already differ by metric doesn't need "HS 6" 1" on every
 * row — so each goes in only while two rows would still read the same without it, and neither ever
 * goes on the badge.
 */
export function planPanelChrome({
    axes,
    streams,
    plotHeightPx,
}: {
    axes: AxisSpec[];
    streams: ReadonlyArray<StreamNames & { axisId: string }>;
    plotHeightPx: number;
}): PanelChrome {
    // A bare number means nothing next to one in a different unit.
    const oneUnit = new Set(axes.map((axis) => axis.unitCategory)).size === 1;

    // Not the same question: two categories that share units go on one axis, so a wind panel is one
    // unit and two metrics.
    const metrics = streams.map((stream) => stream.unitCategory);
    const oneMetric = new Set(metrics).size === 1;
    const deviceNames = streams.map((stream) => stream.name);

    const legend: LegendParts = {
        categories: !oneMetric,
        devices: varies(deviceNames),
        sensorClusters: false,
        sensorElements: false,
    };

    if (!tellsStreamsApart(streams, legend)) {
        legend.sensorClusters = varies(streams.map((s) => s.sensorClusterName ?? null));
    }
    if (!tellsStreamsApart(streams, legend)) {
        legend.sensorElements = varies(streams.map((s) => s.sensorElementName ?? null));
    }

    // One stream per badged axis: each unit is named right beside it, so a bare number still reads.
    const sides = new Set(axes.map((axis) => axis.side));
    const streamAxes = new Set(streams.map((stream) => stream.axisId));
    const badgePerStream = axes.length === 2 && sides.size === 2 && streams.length === 2 && streamAxes.size === 2;

    // Tags fill two columns, one either side of the dot, but edge clipping can push them all onto
    // one side (sideFor falls back to whichever side fits). Stream count is the upper bound for
    // that single-column case, so fitting that many means they always fit.
    const hoverTags = (oneUnit || badgePerStream) && streams.length * TAG_MIN_GAP_PX <= plotHeightPx;

    return {
        metricCategory: oneMetric ? (metrics[0] ?? null) : null,
        deviceName: varies(deviceNames) ? null : (deviceNames[0] ?? null),
        legend,
        hoverTags,
        tooltip: !hoverTags,
    };
}
